import { ApolloError } from '@collinsonx/utils/apollo';
import { BookingError } from 'config/datadog';
import { AVAILABLE_SLOTS_ERRORS } from 'config/graphql';
import fetchErrorObject from 'utils/fetchErrorObject';
import getError from 'utils/getError';

import { HeaderStyle } from './enums';
import { Metadata, PartnerAllowedTimesMetadata } from './types';
import getErrorMessage from 'utils/getErrorMessage';

export function setHeaderTitle(
  headerStyle: HeaderStyle,
  translations: any,
  capacityTitleOverride?: string
): string {
  switch (headerStyle) {
    case HeaderStyle.CAPACITY:
      return capacityTitleOverride ?? translations.capacity.title;
    case HeaderStyle.NO_FLIGHT:
      return translations.noFlightInfoFound.title;
    case HeaderStyle.LOUNGE_CLOSED:
      return translations.loungeClosed.title;
    case HeaderStyle.MAX_PERIOD:
      return translations.maxPeriod.title;
    case HeaderStyle.MIN_PERIOD:
      return translations.minPeriod.title;
    case HeaderStyle.SERVER:
      return translations.server.title;
    case HeaderStyle.UNKNOWN:
      return translations.unavailable.title;
    default:
      return '';
  }
}

export function errorMetadataIsCorrect(
  response: ApolloError | unknown
): boolean {
  const error = fetchErrorObject(response);

  if (!error) return false;

  const errorPropertiesAreInvalid =
    ('code' in error && 'metadata' in error) === false;

  if (errorPropertiesAreInvalid) return false;

  const metadata = error.metadata as Metadata;

  const metadataPropertiesAreInvalid =
    ('adultCount' in metadata &&
      'childrenCount' in metadata &&
      'infantCount' in metadata) === false;

  if (metadataPropertiesAreInvalid) return false;

  const { adultCount, childrenCount, infantCount } = metadata;
  const maxPropertiesAreInvalid =
    ('max' in adultCount && 'max' in childrenCount && 'max' in infantCount) ===
    false;

  return !maxPropertiesAreInvalid;
}

export function hasLoungeCapacityDefaultError(response: ApolloError | unknown) {
  const error = fetchErrorObject(response);
  if (!error) return false;

  if ('code' in error) {
    const { code } = error;

    return code === AVAILABLE_SLOTS_ERRORS.INSUFFICIENT_CAPACITY.code;
  }

  return false;
}

const LOUNGE_CLOSED_ERROR_MESSAGE = 'Lounge is closed';

export function hasLoungeClosedError(response: ApolloError | unknown) {
  const message = getErrorMessage(response);
  if (!message) return false;

  return message.includes(LOUNGE_CLOSED_ERROR_MESSAGE);
}

const HOURS_IN_DAY = 24;

function getPartnerAllowedTimes(
  response: ApolloError | unknown
): PartnerAllowedTimesMetadata | null {
  const error = fetchErrorObject(response);
  if (!error || !('code' in error && 'metadata' in error)) return null;
  if (error.code !== AVAILABLE_SLOTS_ERRORS.PARTNER_ALLOWED_TIMES.code) {
    return null;
  }

  const metadata = error.metadata as {
    partnerAllowedTimes?: PartnerAllowedTimesMetadata;
  } | null;

  return metadata?.partnerAllowedTimes ?? null;
}

export function getMaxPeriodAllowedHours(
  response: ApolloError | unknown
): number | null {
  const hours = getPartnerAllowedTimes(response)?.bookingMaxPeriodAllowed;

  return typeof hours === 'number' && hours > 0 ? hours : null;
}

export function hasMaxPeriodNotAllowedError(
  response: ApolloError | unknown
): boolean {
  return getMaxPeriodAllowedHours(response) !== null;
}

export function getMaxPeriodAllowedDays(
  response: ApolloError | unknown
): number | null {
  const hours = getMaxPeriodAllowedHours(response);

  return hours === null ? null : Math.ceil(hours / HOURS_IN_DAY);
}

export function getMinPeriodAllowedHours(
  response: ApolloError | unknown
): number | null {
  const hours = getPartnerAllowedTimes(response)?.bookingMinPeriodAllowed;

  return typeof hours === 'number' && hours > 0 ? hours : null;
}

export function hasMinPeriodNotAllowedError(
  response: ApolloError | unknown
): boolean {
  return getMinPeriodAllowedHours(response) !== null;
}

export function hasDefaultError(response: ApolloError | unknown): boolean {
  const error = fetchErrorObject(response);
  if (!error) return false;

  if ('code' in error && error.code === BookingError.INTERNAL_SERVER_ERROR) {
    return false;
  }

  return 'code' in error;
}

export function hasInternalServerError(response: unknown): boolean {
  const internalServerError = getError(
    response,
    BookingError.INTERNAL_SERVER_ERROR
  );

  return !!internalServerError;
}

export function hasBadUserInputError(response: unknown): boolean {
  const badUserInputError = getError(response, BookingError.BAD_USER_INPUT);

  return !!badUserInputError;
}
